import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  EXPENSE_TABLE_COLUMN_WIDTHS,
  EXPENSE_TABLE_HEADERS,
  EXPENSE_TABLE_MIN_WIDTH,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/expenses/constants.ts';
import { ExpenseRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/expenses/ExpenseRow.tsx';
import type { IExpenseGroupProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/expenses/types.ts';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import { financialConditionGenerationStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/styles.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { REQUIRED_EXPENSE_REPORT_LINE_CODES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import { createExpense } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const ExpenseGroup: React.FC<IExpenseGroupProps> = ({ name, title, disabled }) => {
  const { control } = useFormContext<TFinancialConditionFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const canRemoveExpense = (reportLine: string) =>
    fields.length > 1 &&
    !REQUIRED_EXPENSE_REPORT_LINE_CODES.includes(
      reportLine.replace(/\D/g, '') as (typeof REQUIRED_EXPENSE_REPORT_LINE_CODES)[number]
    );
  const showActions = fields.some((field) => canRemoveExpense(field.reportLine));

  return (
    <Box sx={financialConditionGenerationStyles.financialAssistanceGroup}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      <FinancialConditionTable
        ariaLabel={title}
        headers={[...EXPENSE_TABLE_HEADERS]}
        showActions={showActions}
        columnWidths={[...EXPENSE_TABLE_COLUMN_WIDTHS]}
        minWidth={EXPENSE_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <ExpenseRow
            key={field.id}
            name={name}
            index={index}
            canRemove={canRemoveExpense(field.reportLine)}
            disabled={disabled}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createExpense())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати витрату
      </Button>
    </Box>
  );
};
